import React, { useState } from 'react';
import { formatFileSize } from './imageUtils';
import { getPendingFileMetadata } from './pendingFiles';

/** Detect whether a media URL points to a video file. */
function isVideoMedia(url: string): boolean {
  if (url.startsWith('blob:')) {
    const meta = getPendingFileMetadata(url);
    return !!meta?.isVideo;
  }
  if (url.startsWith('data:video/')) return true;
  return /\.(mp4|webm|mov)(\?|$)/i.test(url);
}

export interface ImagePreviewData {
  id: string;
  url: string;
  thumbnail?: string;
  filename: string;
  size: number;
  isUploading?: boolean;
  progress?: number;
  error?: string;
  isMain?: boolean;
  copyright?: string;
}

interface ImagePreviewProps {
  image: ImagePreviewData;
  onDelete: (id: string) => void;
  onRetry?: (id: string) => void;
  onCopyrightChange?: (id: string, copyright: string) => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, image: ImagePreviewData) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, targetImage: ImagePreviewData) => void;
  className?: string;
}

export const ImagePreview: React.FC<ImagePreviewProps> = ({
  image,
  onDelete,
  onRetry,
  onCopyrightChange,
  draggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  className = '',
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isEditingCopyright, setIsEditingCopyright] = useState(false);
  const [copyrightDraft, setCopyrightDraft] = useState(image.copyright ?? '');

  const openCopyrightEditor = () => {
    setCopyrightDraft(image.copyright ?? '');
    setIsEditingCopyright(true);
  };

  const commitCopyright = () => {
    const next = copyrightDraft.trim();
    if (next !== (image.copyright ?? '') && onCopyrightChange) {
      onCopyrightChange(image.id, next);
    }
    setIsEditingCopyright(false);
  };

  const cancelCopyright = () => {
    setCopyrightDraft(image.copyright ?? '');
    setIsEditingCopyright(false);
  };

  const handleDelete = () => {
    if (showConfirm) {
      onDelete(image.id);
      setShowConfirm(false);
    } else {
      setShowConfirm(true);
      // Auto-hide confirm after 3s
      setTimeout(() => setShowConfirm(false), 3000);
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (draggable && onDragStart) {
      onDragStart(e, image);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (draggable && onDragOver) {
      e.preventDefault();
      setIsDragOver(true);
      onDragOver(e);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (draggable && onDrop) {
      e.preventDefault();
      setIsDragOver(false);
      onDrop(e, image);
    }
  };

  return (
    <div
      draggable={draggable && !image.isUploading}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        relative
        group
        rounded-lg
        overflow-hidden
        bg-gray-100
        transition-all duration-200
        ${draggable ? 'cursor-move' : ''}
        ${isDragOver ? 'ring-2 ring-balloon scale-105' : ''}
        ${image.error ? 'ring-2 ring-red-500' : ''}
        ${className}
      `}
    >
      {/* Media */}
      <div className="aspect-square relative">
        {isVideoMedia(image.url) ? (
          <video
            src={image.url}
            className="w-full h-full object-cover"
            muted
            loop
            playsInline
            controls
          />
        ) : (
          <img
            src={image.thumbnail || image.url}
            alt={image.filename}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}

        {/* Upload overlay */}
        {image.isUploading && (
          <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white">
            <div className="text-sm font-medium mb-2">Uploading...</div>
            <div className="w-3/4 h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-balloon transition-all duration-300"
                style={{ width: `${image.progress || 0}%` }}
              />
            </div>
            <div className="text-xs mt-2">{image.progress || 0}%</div>
          </div>
        )}

        {/* Error overlay */}
        {image.error && (
          <div className="absolute inset-0 bg-red-500/90 flex flex-col items-center justify-center text-white p-2">
            <div className="text-2xl mb-2">⚠️</div>
            <div className="text-xs text-center mb-2">{image.error}</div>
            {onRetry && (
              <button
                onClick={() => onRetry(image.id)}
                className="px-3 py-1 bg-white text-red-500 rounded text-xs font-medium hover:bg-gray-100"
              >
                Retry
              </button>
            )}
          </div>
        )}

        {/* Main badge */}
        {image.isMain && !image.isUploading && !image.error && (
          <div className="absolute top-2 left-2 px-2 py-1 bg-balloon text-white text-xs font-medium rounded-full">
            Main Image
          </div>
        )}

        {/* Delete button */}
        {!image.isUploading && (
          <button
            onClick={handleDelete}
            className={`
              absolute top-2 right-2
              w-8 h-8
              rounded-full
              flex items-center justify-center
              transition-all duration-200
              ${showConfirm ? 'bg-red-500 text-white' : 'bg-black/50 text-white opacity-0 group-hover:opacity-100'}
              hover:scale-110
            `}
            title={showConfirm ? 'Click again to confirm' : 'Delete image'}
            aria-label={showConfirm ? 'Confirm delete' : 'Delete image'}
          >
            {showConfirm ? '✓' : '✕'}
          </button>
        )}

        {/* Drag handle */}
        {draggable && !image.isUploading && !image.error && (
          <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="bg-black/50 text-white px-2 py-1 rounded text-xs">
              ⋮⋮
            </div>
          </div>
        )}

        {/* Copyright chip / inline editor */}
        {!image.isUploading && !image.error && onCopyrightChange && (
          isEditingCopyright ? (
            <input
              type="text"
              value={copyrightDraft}
              autoFocus
              onChange={(e) => setCopyrightDraft(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onBlur={commitCopyright}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitCopyright();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelCopyright();
                }
              }}
              placeholder="Photographer"
              aria-label="Photo credit"
              className="absolute bottom-2 left-2 max-w-[80%] rounded bg-black/70 text-white text-[11px] px-2 py-0.5 outline-none placeholder-white/60 focus:ring-2 focus:ring-balloon"
            />
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openCopyrightEditor(); }}
              aria-label={image.copyright ? 'Edit photo credit' : 'Add photo credit'}
              title={image.copyright ? `© ${image.copyright}` : 'Add photo credit'}
              className="absolute bottom-2 left-2 max-w-[60%] truncate rounded bg-black/50 text-white text-[11px] px-2 py-0.5 hover:bg-black/70 transition-colors"
            >
              {image.copyright ? `© ${image.copyright}` : '©'}
            </button>
          )
        )}
      </div>

      {/* Metadata */}
      <div className="p-2 bg-white">
        <p className="text-xs font-medium text-gray-700 truncate" title={image.filename}>
          {image.filename}
        </p>
        <p className="text-xs text-gray-500">{formatFileSize(image.size)}</p>
      </div>
    </div>
  );
};
